/**
 * Leviathan Web - Email Template Form Page
 * Quasar Prime LLC
 *
 * Create or edit email templates with live preview
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import {
  Mail,
  Save,
  ArrowLeft,
  Eye,
  Code,
  AlertCircle,
  Info,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  subject: string;
  htmlBody: string;
  textBody: string | null;
  isActive: boolean;
  isSystem: boolean;
  category: string;
}

const CATEGORIES = [
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'STATEMENT', label: 'Statement' },
  { value: 'DELIVERY', label: 'Delivery' },
  { value: 'ORDER', label: 'Order' },
  { value: 'NOTIFICATION', label: 'Notification' },
  { value: 'CUSTOM', label: 'Custom' },
];

const SAMPLE_VARIABLES: Record<string, Record<string, string>> = {
  INVOICE: {
    customerName: 'Acme Fuel Corp',
    invoiceNumber: 'INV-2024-0001',
    invoiceDate: '2024-01-15',
    dueDate: '2024-02-15',
    invoiceTotal: '$5,432.00',
    companyName: 'Quasar Prime LLC',
  },
  STATEMENT: {
    customerName: 'Acme Fuel Corp',
    statementDate: '2024-01-31',
    beginningBalance: '$10,000.00',
    totalCharges: '$5,000.00',
    totalPayments: '$3,000.00',
    endingBalance: '$12,000.00',
    companyName: 'Quasar Prime LLC',
  },
  DELIVERY: {
    customerName: 'Acme Fuel Corp',
    bolNumber: 'BOL-2024-0001',
    deliveryDate: '2024-01-15',
    driverName: 'John Smith',
    productName: 'Ultra Low Sulfur Diesel',
    quantity: '2,500 gallons',
    companyName: 'Quasar Prime LLC',
  },
  DEFAULT: {
    customerName: 'Customer Name',
    companyName: 'Quasar Prime LLC',
    date: '2024-01-15',
  },
};

export default function EmailTemplateForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();
  const isEditing = id && id !== 'new';

  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    subject: '',
    htmlBody: '',
    textBody: '',
    category: 'CUSTOM',
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  // Fetch existing template
  const { data: template, isLoading } = useQuery({
    queryKey: ['email-template', id],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/email/templates/${id}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch template');
      return response.json() as Promise<EmailTemplate>;
    },
    enabled: !!isEditing && !!accessToken,
  });

  // Populate form with existing data
  useEffect(() => {
    if (template) {
      setFormData({
        code: template.code,
        name: template.name,
        description: template.description || '',
        subject: template.subject,
        htmlBody: template.htmlBody,
        textBody: template.textBody || '',
        category: template.category,
        isActive: template.isActive,
      });
    }
  }, [template]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const url = isEditing
        ? `${API_URL}/api/email/templates/${id}`
        : `${API_URL}/api/email/templates`;
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      navigate('/settings/email/templates');
    },
    onError: (error: Error) => {
      setErrors({ general: error.message });
    },
  });

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    const newErrors: Record<string, string> = {};
    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.htmlBody.trim()) newErrors.htmlBody = 'HTML body is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    saveMutation.mutate(formData);
  };

  // Replace template variables with sample values for preview
  const getPreviewContent = (content: string): string => {
    const variables: Record<string, string> =
      SAMPLE_VARIABLES[formData.category] ?? SAMPLE_VARIABLES.DEFAULT!;
    let preview = content;
    Object.entries(variables).forEach(([key, value]) => {
      preview = preview.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return preview;
  };

  // Available variables helper
  const availableVariables: Record<string, string> =
    SAMPLE_VARIABLES[formData.category] ?? SAMPLE_VARIABLES.DEFAULT!;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/settings/email/templates')}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex h-10 w-10 items-center justify-center bg-amber-500/20 border border-amber-500/30">
            <Mail className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">
              {isEditing ? 'Edit Template' : 'New Template'}
            </h1>
            <p className="text-sm text-slate-400">
              {isEditing
                ? `Editing ${template?.name}`
                : 'Create a new email template'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
              showPreview
                ? 'bg-amber-500/20 border-amber-500/30 text-amber-500'
                : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {showPreview ? <Code className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* System Template Warning */}
      {template?.isSystem && (
        <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-blue-400 font-medium">System Template</div>
            <p className="text-sm text-slate-400">
              This is a system template. You can modify the content, but the code
              cannot be changed.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="bg-slate-800/50 border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                Basic Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Template Code *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                      })
                    }
                    disabled={template?.isSystem}
                    placeholder="INVOICE_REMINDER"
                    className={`w-full px-3 py-2 bg-slate-900 border text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 font-mono ${
                      errors.code ? 'border-red-500' : 'border-slate-600'
                    } ${template?.isSystem ? 'opacity-50 cursor-not-allowed' : ''}`}
                  />
                  {errors.code && (
                    <p className="text-red-400 text-sm mt-1">{errors.code}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white focus:outline-none focus:border-amber-500"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Invoice Reminder Email"
                    className={`w-full px-3 py-2 bg-slate-900 border text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 ${
                      errors.name ? 'border-red-500' : 'border-slate-600'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Sent when an invoice is past due"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div className="md:col-span-2 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="h-4 w-4 bg-slate-900 border-slate-600 text-amber-500 focus:ring-amber-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-slate-300">
                    Template is active and can be used for sending emails
                  </label>
                </div>
              </div>
            </div>

            {/* Subject */}
            <div className="bg-slate-800/50 border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                Email Subject *
              </h2>
              {showPreview ? (
                <div className="px-3 py-2 bg-slate-900 border border-slate-600 text-white">
                  {getPreviewContent(formData.subject) || 'No subject'}
                </div>
              ) : (
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                  placeholder="Invoice {{invoiceNumber}} from {{companyName}}"
                  className={`w-full px-3 py-2 bg-slate-900 border text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 ${
                    errors.subject ? 'border-red-500' : 'border-slate-600'
                  }`}
                />
              )}
              {errors.subject && (
                <p className="text-red-400 text-sm mt-1">{errors.subject}</p>
              )}
            </div>

            {/* HTML Body */}
            <div className="bg-slate-800/50 border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                HTML Body *
              </h2>
              {showPreview ? (
                <div className="bg-white p-4 min-h-[300px]">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: getPreviewContent(formData.htmlBody),
                    }}
                  />
                </div>
              ) : (
                <textarea
                  value={formData.htmlBody}
                  onChange={(e) =>
                    setFormData({ ...formData, htmlBody: e.target.value })
                  }
                  rows={15}
                  placeholder="<html>...</html>"
                  className={`w-full px-3 py-2 bg-slate-900 border text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 font-mono text-sm ${
                    errors.htmlBody ? 'border-red-500' : 'border-slate-600'
                  }`}
                />
              )}
              {errors.htmlBody && (
                <p className="text-red-400 text-sm mt-1">{errors.htmlBody}</p>
              )}
            </div>

            {/* Plain Text Body */}
            <div className="bg-slate-800/50 border border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                Plain Text Body (Optional)
              </h2>
              <p className="text-sm text-slate-400 mb-4">
                Fallback for email clients that don't support HTML
              </p>
              {showPreview ? (
                <pre className="px-3 py-2 bg-slate-900 border border-slate-600 text-white whitespace-pre-wrap font-mono text-sm min-h-[100px]">
                  {getPreviewContent(formData.textBody) || 'No plain text body'}
                </pre>
              ) : (
                <textarea
                  value={formData.textBody}
                  onChange={(e) =>
                    setFormData({ ...formData, textBody: e.target.value })
                  }
                  rows={6}
                  placeholder="Plain text version of your email..."
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 font-mono text-sm"
                />
              )}
            </div>
          </form>
        </div>

        {/* Sidebar - Available Variables */}
        <div className="lg:col-span-1">
          <div className="bg-slate-800/50 border border-slate-700 p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Available Variables
            </h2>
            <p className="text-sm text-slate-400 mb-4">
              Use these variables in your template. They will be replaced with
              actual values when the email is sent.
            </p>
            <div className="space-y-2">
              {Object.entries(availableVariables).map(([key, sampleValue]) => (
                <div
                  key={key}
                  className="p-2 bg-slate-900 border border-slate-700 hover:border-slate-600 cursor-pointer transition-colors"
                  onClick={() => navigator.clipboard.writeText(`{{${key}}}`)}
                  title="Click to copy"
                >
                  <code className="text-amber-400 text-sm">{`{{${key}}}`}</code>
                  <div className="text-xs text-slate-500 mt-1">
                    Example: {sampleValue}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-300">
                  <strong className="text-amber-500">Tip:</strong> Click any
                  variable to copy it to your clipboard, then paste it into your
                  template.
                </div>
              </div>
            </div>

            {/* Common Variables */}
            <div className="mt-6">
              <h3 className="text-sm font-medium text-slate-300 mb-3">
                Common Variables
              </h3>
              <div className="space-y-2 text-sm text-slate-400">
                <div>
                  <code className="text-amber-400">{`{{companyName}}`}</code> -
                  Your company name
                </div>
                <div>
                  <code className="text-amber-400">{`{{companyPhone}}`}</code> -
                  Company phone
                </div>
                <div>
                  <code className="text-amber-400">{`{{companyEmail}}`}</code> -
                  Company email
                </div>
                <div>
                  <code className="text-amber-400">{`{{companyAddress}}`}</code>{' '}
                  - Company address
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
